import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AlertCircle, Search, SearchX } from 'lucide-react';

import { Breadcrumb } from '../components/Breadcrumb';
import { PurchaseOrderReceiptsTable } from '../components/purchaseOrders/PurchaseOrderReceiptsTable';
import { apiService } from '../services/apiService';
import { getCachedData, setCachedData } from '../utils/pageCache';
import type { PurchaseOrder, PurchaseOrderLine } from '../services/types';
import { parseCalendarDate } from '../utils/calendarDate';

/**
 * Helper function to format date string to a more readable format
 */
function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const date = parseCalendarDate(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-CA');
}

/**
 * Helper function to format an amount with currency (space thousands separator)
 */
function formatPrice(value: number, currency: string, decimals = 2): string {
  const formatted = value.toFixed(decimals);
  const [dollars, cents] = formatted.split('.');
  const dollarsParts: string[] = [];
  let count = 0;
  for (let i = dollars.length - 1; i >= 0; i--) {
    if (count === 3) {
      dollarsParts.unshift(' ');
      count = 0;
    }
    dollarsParts.unshift(dollars[i]);
    count++;
  }
  return `${dollarsParts.join('')}.${cents} ${currency}`;
}

/**
 * Unit costs can carry more precision than cents (e.g. 0.0125 per each)
 */
function formatUnitCost(value: number, currency: string): string {
  const needsMorePrecision =
    Number(value.toFixed(2)) !== Number(value.toFixed(4));
  return formatPrice(value, currency, needsMorePrecision ? 4 : 2);
}

const LINE_TYPE_KEYS: Record<PurchaseOrderLine['lineType'], [string, string]> =
  {
    inventory: ['purchaseOrder.lineTypeInventory', 'Inventory'],
    subcontract: ['purchaseOrder.lineTypeSubcontract', 'Subcontract'],
    nonStock: ['purchaseOrder.lineTypeNonStock', 'Non-stock'],
    other: ['purchaseOrder.lineTypeOther', 'Other'],
  };

/**
 * Format a quantity, keeping up to 2 decimals for fractional quantities
 */
function formatQty(value: number): string {
  return Number(value.toFixed(2)).toString();
}

/**
 * Get status badge based on PO header status
 */
function getStatusBadge(
  openOrder: boolean,
  voidOrder: boolean,
  orderHeld: boolean
): { text: string; className: string } {
  if (voidOrder)
    return { text: 'Void', className: 'bg-gray-100 text-gray-800' };
  if (orderHeld)
    return { text: 'On Hold', className: 'bg-yellow-100 text-yellow-800' };
  if (openOrder)
    return { text: 'Open', className: 'bg-green-100 text-green-800' };
  return { text: 'Closed', className: 'bg-blue-100 text-blue-800' };
}

/**
 * Get status badge for a PO line
 */
function getLineStatusBadge(
  open: boolean,
  isVoid: boolean
): { text: string; className: string } {
  if (isVoid) return { text: 'Void', className: 'bg-gray-100 text-gray-800' };
  if (!open) return { text: 'Closed', className: 'bg-blue-100 text-blue-800' };
  return { text: 'Open', className: 'bg-green-100 text-green-800' };
}

export const PurchaseOrderTrackerPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { poNum: poNumParam } = useParams<{ poNum: string }>();
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFoundPoNum, setNotFoundPoNum] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');

  const fetchPurchaseOrder = useCallback(
    async (poNum: string) => {
      setNotFoundPoNum(null);

      const poNumInt = parseInt(poNum, 10);
      if (!/^\d+$/.test(poNum) || isNaN(poNumInt) || poNumInt <= 0) {
        setError(
          t(
            'purchaseOrder.invalidPoNumber',
            'Invalid PO number. PO numbers contain digits only.'
          )
        );
        setPurchaseOrder(null);
        setIsLoading(false);
        return;
      }

      // Check cache first
      const cacheKey = `purchaseOrder_${poNumInt}`;
      const cached = getCachedData<PurchaseOrder>(cacheKey);
      if (cached) {
        setPurchaseOrder(cached);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const data = await apiService.getPurchaseOrder(poNumInt);
        setPurchaseOrder(data);
        // Cache the result
        setCachedData(cacheKey, data);
      } catch (err) {
        setPurchaseOrder(null);
        if ((err as { status?: number } | undefined)?.status === 404) {
          // Clean empty state instead of the raw API message
          setNotFoundPoNum(String(poNumInt));
        } else {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to fetch purchase order'
          );
        }
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (poNumParam) {
      setSearchValue(poNumParam);
      fetchPurchaseOrder(poNumParam.trim());
    }
  }, [poNumParam, fetchPurchaseOrder]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      // Update URL and fetch data - encode to handle special characters
      navigate(`/purchase-orders/${encodeURIComponent(searchValue.trim())}`);
    }
  };

  const { header, lines } = purchaseOrder || { header: null, lines: [] };
  const statusBadge = header
    ? getStatusBadge(header.openOrder, header.voidOrder, header.orderHeld)
    : null;

  const headerCellClass =
    'px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-tight';

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 w-full">
      <div className="w-full">
        <Breadcrumb currentPage={t('purchaseOrder.tracker')} />

        {/* Header */}
        <div className="mb-8 mt-4 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('purchaseOrder.tracker')}
            </h1>
            <p className="mt-2 text-gray-600">
              {t(
                'purchaseOrder.trackerDescription',
                'View and track purchase order details'
              )}
            </p>
          </div>
        </div>

        {/* Search Component */}
        <div className="space-y-6">
          {/* Search Input with Button */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input
                type="text"
                inputMode="numeric"
                placeholder={t(
                  'purchaseOrder.searchPlaceholder',
                  'Enter PO number (e.g., 600123)'
                )}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch(e)}
                disabled={isLoading}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={isLoading || !searchValue.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors"
            >
              {isLoading
                ? t('parts.search.searching', 'Searching...')
                : t('common.search', 'Search')}
            </button>
          </div>

          {/* Error Message */}
          {error && !isLoading && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-900">
                  {t('common.error', 'Error')}
                </h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                {t('parts.search.searching', 'Searching...')}...
              </p>
            </div>
          )}

          {/* Not Found State */}
          {notFoundPoNum && !isLoading && (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <SearchX className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-900 font-medium">
                {t('purchaseOrder.notFound', 'Purchase Order not found')}
              </p>
              <p className="text-gray-600 text-sm mt-1">
                {t('purchaseOrder.notFoundDetail', {
                  poNum: notFoundPoNum,
                  defaultValue:
                    'No purchase order matches PO {{poNum}}. Check the number and try again.',
                })}
              </p>
            </div>
          )}

          {/* Initial State - Prompting for Search */}
          {!purchaseOrder && !isLoading && !error && !notFoundPoNum && (
            <div className="text-center py-12 bg-blue-50 rounded-lg border border-blue-200">
              <Search className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <p className="text-blue-900 font-medium">
                {t(
                  'purchaseOrder.enterPoNumber',
                  'Enter a PO number above to view purchase order details'
                )}
              </p>
              <p className="text-blue-700 text-sm mt-1">
                {t(
                  'purchaseOrder.searchInstructions',
                  'Search by PO number to view the PO header and lines'
                )}
              </p>
            </div>
          )}

          {/* Purchase Order Content */}
          {purchaseOrder && header && !isLoading && (
            <>
              {/* PO Header Section */}
              <div className="bg-white rounded border border-gray-300 shadow-sm">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-blue-900">
                      {t('purchaseOrder.poHeader', 'PO Header')}
                    </h2>
                    <p className="text-xs text-blue-700">
                      {header.vendorName}
                      {header.vendorId && ` (${header.vendorId})`}
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadge?.className}`}
                  >
                    {statusBadge?.text}
                  </span>
                </div>

                <div className="p-4 space-y-6">
                  {/* Three Column Layout: Supplier, Shipping & Terms, General Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Supplier Section */}
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <h3 className="text-sm font-bold text-green-900 uppercase tracking-tight mb-3">
                        {t('purchaseOrder.supplier', 'Supplier')}
                      </h3>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-900">
                          {header.vendorName || '—'}
                          {header.vendorId && (
                            <span className="text-gray-600 font-normal">
                              {' '}
                              ({header.vendorId})
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-700">
                          <span className="font-medium">
                            {t('purchaseOrder.buyer', 'Buyer')}:
                          </span>{' '}
                          {header.buyerName || header.buyerId || '—'}
                        </p>
                      </div>
                    </div>

                    {/* Shipping & Terms Section */}
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <h3 className="text-sm font-bold text-purple-900 uppercase tracking-tight mb-3">
                        {t(
                          'purchaseOrder.shippingAndTerms',
                          'Shipping & Terms'
                        )}
                      </h3>
                      <div className="grid grid-cols-1 gap-y-2 text-xs">
                        <div>
                          <span className="text-gray-600">
                            {t('purchaseOrder.shipVia', 'Ship Via')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {header.shipVia || '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('purchaseOrder.fob', 'FOB')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {header.fob || '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('purchaseOrder.terms', 'Terms')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {header.terms || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* General Info Section */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-tight mb-3">
                        {t('purchaseOrder.generalInfo', 'General Info')}
                      </h3>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                        <div>
                          <span className="text-gray-600">
                            {t('purchaseOrder.poNumber', 'PO #')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {header.poNum}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('purchaseOrder.orderDate', 'Order Date')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {formatDate(header.orderDate)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('purchaseOrder.currency', 'Currency')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {header.currencyCode || '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('purchaseOrder.entryPerson', 'Entry')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {header.entryPerson || '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('purchaseOrder.totalLines', 'Lines')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {lines.length}
                          </span>
                        </div>
                        <div className="col-span-2 pt-2 border-t border-gray-200 mt-2">
                          <span className="text-gray-600">
                            {t('purchaseOrder.total', 'Total')}:
                          </span>
                          <span className="ml-1 font-bold text-gray-900">
                            {formatPrice(
                              header.docTotalOrder,
                              header.currencyCode
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Comment */}
                  {header.comment && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                        {t('purchaseOrder.comment', 'Comment')}
                      </p>
                      <p className="text-sm text-gray-700 whitespace-pre-line">
                        {header.comment}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* PO Lines Section */}
              <div className="bg-white rounded border border-gray-300 shadow-sm">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
                  <h2 className="text-lg font-bold text-blue-900">
                    {t('purchaseOrder.poLines', 'PO Lines')} ({lines.length})
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className={`${headerCellClass} text-left`}>
                          {t('purchaseOrder.line', 'Line')}
                        </th>
                        <th className={`${headerCellClass} text-left`}>
                          {t('purchaseOrder.partNum', 'Part #')}
                        </th>
                        <th className={`${headerCellClass} text-left`}>
                          {t('purchaseOrder.description', 'Description')}
                        </th>
                        <th className={`${headerCellClass} text-left`}>
                          {t('purchaseOrder.lineType', 'Type')}
                        </th>
                        <th className={`${headerCellClass} text-left`}>
                          {t('purchaseOrder.dueDate', 'Due Date')}
                        </th>
                        <th className={`${headerCellClass} text-right`}>
                          {t('purchaseOrder.qty', 'Qty')}
                        </th>
                        <th className={`${headerCellClass} text-right`}>
                          {t('purchaseOrder.received', 'Received')}
                        </th>
                        <th className={`${headerCellClass} text-left`}>
                          {t('purchaseOrder.uom', 'UOM')}
                        </th>
                        <th className={`${headerCellClass} text-right`}>
                          {t('purchaseOrder.unitCost', 'Unit Cost')}
                        </th>
                        <th className={`${headerCellClass} text-right`}>
                          {t('purchaseOrder.extCost', 'Ext Cost')}
                        </th>
                        <th className={`${headerCellClass} text-center`}>
                          {t('purchaseOrder.status', 'Status')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line: PurchaseOrderLine) => {
                        const lineStatus = getLineStatusBadge(
                          line.openLine,
                          line.voidLine
                        );
                        // Releases are not listed: in practice each PO line has a single one
                        const totalReceived = line.releases.reduce(
                          (sum, rel) => sum + rel.receivedQty,
                          0
                        );

                        return (
                          <tr
                            key={line.poLine}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 text-gray-900 font-medium">
                              {line.poLine}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-col items-start gap-1">
                                {line.partNum ? (
                                  <Link
                                    to={`/parts/${encodeURIComponent(line.partNum)}`}
                                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                  >
                                    {line.partNumUM || line.partNum}
                                  </Link>
                                ) : (
                                  <span className="text-gray-900 font-medium">
                                    {line.partNumUM || '—'}
                                  </span>
                                )}
                                {line.venPartNum && (
                                  <span
                                    className="text-xs text-gray-600"
                                    title={t(
                                      'purchaseOrder.supplierPart',
                                      'Supplier Part'
                                    )}
                                  >
                                    {line.venPartNum}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-700 max-w-xs truncate">
                              {line.lineDesc}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex flex-col items-start gap-1">
                                <span className="text-gray-900">
                                  {t(...LINE_TYPE_KEYS[line.lineType])}
                                </span>
                                {line.jobNum && (
                                  <Link
                                    to={`/job-tracker?jobNum=${encodeURIComponent(line.jobNum)}`}
                                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    {t('purchaseOrder.jobOperation', {
                                      jobNum: line.jobNum,
                                      opr: line.jobOprSeq,
                                    })}
                                  </Link>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                              {formatDate(line.dueDate)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 font-medium">
                              {formatQty(line.orderQty)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900">
                              {formatQty(totalReceived)}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {line.uom}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 whitespace-nowrap">
                              {formatUnitCost(
                                line.docUnitCost,
                                header.currencyCode
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900 whitespace-nowrap">
                              {formatPrice(
                                line.docExtCost,
                                header.currencyCode
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${lineStatus.className}`}
                              >
                                {lineStatus.text}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {lines.length === 0 && (
                    <div className="p-8 text-center text-gray-500">
                      {t('purchaseOrder.noLines', 'No PO lines found')}
                    </div>
                  )}
                </div>
              </div>

              <PurchaseOrderReceiptsTable poNum={header.poNum} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
